import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Theme, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AxiosError } from 'axios';
import { makeStyles } from 'tss-react/mui';
import Loader from '../../components/Loader';
import TablePaginationLocalContainer from '../../components/Table/TablePaginationLocalContainer';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants/constants';
import { getAutoSweepBatchDetails, exportAutoSweepTransactions } from '../../api/bankingApi/bankingApi';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { formatDate } from '../../helpers/formatDate';
import useSnackBar from '../../hooks/useSnackBar';
import FormSearchField from '../../components/formFields/FormSearchField';
import ViewCommonButton from '../../components/buttons/ViewCommonButton';
import { ErrorData } from '../../types';

const useStyles = makeStyles()((theme: Theme) => ({
  page: { padding: '32px', color: theme.palette.common.white, fontFamily: 'Poppins' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 600 },
  toolbar: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  search: { minWidth: '280px', flexGrow: 1 },
  table: { background: 'transparent', border: `1px solid ${theme.palette.secondary.dark}`, borderRadius: '8px', overflow: 'auto', maxHeight: 'calc(100vh - 300px)' },
  headerCell: { background: theme.palette.primary.light, color: theme.palette.text.secondary, fontWeight: 500, padding: '14px 16px' },
  bodyCell: { color: theme.palette.common.white, borderBottom: `1px solid ${theme.palette.secondary.dark}`, padding: '16px' },
  empty: { textAlign: 'center', color: theme.palette.text.secondary, padding: '48px' },
}));

const AutoSweepBatchDetailsPage = () => {
  const { batchCode = '' } = useParams<{ batchCode: string }>();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const { classes } = useStyles();
  const firmId = localStorage.getItem('firmId');
  const [search, setSearch] = useState('');
  // Debounced from `search` below. Typing fast used to fire one request per keystroke; whichever
  // response happened to resolve last won, regardless of whether it matched the current input —
  // so a stale response for an earlier (or since-cleared) search could overwrite real results
  // with "no results". Debouncing cuts the request volume; the ignore-flag in the effect below
  // is what actually guarantees a stale response can never overwrite a newer one.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<'amountDesc' | 'amountAsc'>('amountDesc');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [data, setData] = useState<Awaited<ReturnType<typeof getAutoSweepBatchDetails>>['data'] | null>(null);
  const [loading, setLoading] = useState(false);
  const searchForm = useForm({ defaultValues: { batchSearch: '' } });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!firmId || !batchCode) return;
    let ignore = false;
    setLoading(true);
    getAutoSweepBatchDetails(Number(firmId), batchCode, page + 1, size, debouncedSearch, undefined, sort)
      .then((response) => { if (!ignore) setData(response.data); })
      .catch((error) => { if (!ignore) setAlert((previous) => ({ ...previous, message: error.response?.data?.message || 'Failed to load batch details.', type: 'error' })); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [firmId, batchCode, page, size, debouncedSearch, sort, setAlert]);

  const toggleSort = () => {
    setSort((current) => current === 'amountDesc' ? 'amountAsc' : 'amountDesc');
    setPage(0);
  };

  const exportBatch = async () => {
    if (!firmId || !batchCode) return;
    try {
      const response = await exportAutoSweepTransactions({
        firmId: Number(firmId),
        batchCode,
        search,
        contributionSort: sort,
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `autosweep-batch-${batchCode}-transactions.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((previous) => ({ ...previous, message: axiosError.response?.data?.message || 'Failed to export transactions.', type: 'error' }));
    }
  };

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(-1)} aria-label="Go back" color="inherit"><ArrowBackIcon /></IconButton>
          <Box><Typography className={classes.title}>Autosweep batch {batchCode}</Typography><Typography>Total: {data ? numberToUSDCurrency(data.amount) : '-'}</Typography><Typography>Last updated: {data?.updatedAt ? formatDate(data.updatedAt) : '-'}</Typography></Box>
        </Box>
        <ViewCommonButton type="export" onClick={exportBatch} />
      </Box>
      <Box className={classes.toolbar}>
        <FormProvider {...searchForm}>
          <FormSearchField
            name="batchSearch"
            searchValue={search}
            handleChange={(value) => { setSearch(value); setPage(0); }}
            handleResetSearch={() => { setSearch(''); setPage(0); searchForm.setValue('batchSearch', ''); }}
          />
        </FormProvider>
      </Box>
      <TableContainer className={classes.table} component={Paper} elevation={0}>
        <Table stickyHeader>
          <TableHead><TableRow><TableCell className={classes.headerCell}>Plaintiff Name</TableCell><TableCell className={classes.headerCell}>Case Reference ID</TableCell><TableCell className={classes.headerCell}>Type</TableCell><TableCell className={classes.headerCell}><TableSortLabel active direction={sort === 'amountAsc' ? 'asc' : 'desc'} onClick={toggleSort}>Amount</TableSortLabel></TableCell></TableRow></TableHead>
          <TableBody>{loading ? <TableRow><TableCell colSpan={4} className={classes.empty}><Loader colorType="warning" size={40} /></TableCell></TableRow> : !data?.contributions.length ? <TableRow><TableCell colSpan={4} className={classes.empty}>No contributions found</TableCell></TableRow> : data.contributions.map((contribution, index) => <TableRow key={`${contribution.settlementId}-${index}`}><TableCell className={classes.bodyCell}>{contribution.plaintiffName || '-'}</TableCell><TableCell className={classes.bodyCell}>{contribution.caseReferenceId || '-'}</TableCell><TableCell className={classes.bodyCell}>{contribution.attyFeeId != null ? 'Attorney Fee' : contribution.deductionId != null ? 'Deduction' : 'Settlement'}</TableCell><TableCell className={classes.bodyCell}>{numberToUSDCurrency(contribution.amount)}</TableCell></TableRow>)}</TableBody>
        </Table>
      </TableContainer>
      <TablePaginationLocalContainer count={data?.total || 0} page={page} rowsPerPage={size} onPageChange={(_, nextPage) => setPage(nextPage)} onRowsPerPageChange={(event) => { setSize(parseInt(event.target.value as string, 10)); setPage(0); }} totalPages={Math.ceil((data?.total || 0) / size)} />
    </Box>
  );
};

export default AutoSweepBatchDetailsPage;
